import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Lightbulb, Pencil, Hammer, CheckCircle } from 'lucide-react';

const steps = [
  {
    id: 1,
    title: 'Understanding',
    description: 'We begin by understanding your space, lifestyle, and expectations.',
    icon: Lightbulb,
  },
  {
    id: 2,
    title: 'Design',
    description: 'Layouts, materials, and details are developed with clarity and intent.',
    icon: Pencil,
  },
  {
    id: 3,
    title: 'Execution',
    description: 'Our team ensures that what is designed is executed without compromise.',
    icon: Hammer,
  },
  {
    id: 4,
    title: 'Delivery',
    description: 'A finished space that feels cohesive, refined, and ready to live in.',
    icon: CheckCircle,
  },
];

export const ProcessSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      data-testid="process-section"
      className="section-padding bg-[#D9CFC0]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="overline text-[#B5703A] block mb-4">How We Work</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#2A2A2A] tracking-tight mb-6">
              A Structured<br />
              Approach to Craft
            </h2>
            <p className="text-[#3D3D3D] font-light max-w-md leading-relaxed">
              From first conversation to final walkthrough, every project follows a clear, 
              considered process designed to bring your vision to life.
            </p>
          </motion.div>

          {/* Steps */}
          <div className="relative">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: 30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="relative pl-16 pb-10 last:pb-0"
                  data-testid={`process-step-${step.id}`}
                >
                  {/* Timeline connector */}
                  {index < steps.length - 1 && (
                    <div className="absolute left-[19px] top-12 bottom-0 w-px bg-[#C4B6A4]" />
                  )}
                  
                  {/* Icon */}
                  <div className="absolute left-0 top-0 w-10 h-10 bg-[#F5F0E8] border border-[#C4B6A4] flex items-center justify-center">
                    <Icon size={18} className="text-[#B5703A]" />
                  </div>

                  {/* Content */}
                  <div>
                    <span className="text-xs text-[#B5703A] tracking-widest uppercase">
                      Step {step.id}
                    </span>
                    <h3 className="text-xl font-light text-[#2A2A2A] mt-1 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-[#3D3D3D] font-light leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
